import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { STORAGE_KEY_AUTH } from 'src/app/constants';
import { UserLoginResponse } from 'src/_api/models/user-login-response';
import { UserRoleEnum } from 'src/app/admin/user/model/user.model';
import { AuthService } from '../auth.service';

type HandoffState = 'opening' | 'failed' | 'notAdmin';

// The API bounces an unvalidated dashboard cookie straight back here, so without a marker the two
// redirect at each other indefinitely. Timestamped rather than a flag because a successful hand-off
// leaves the app entirely and never gets to clear it.
const ATTEMPT_KEY = 'flow.hangfireHandoffAttemptedAt';
const ATTEMPT_WINDOW_MS = 30_000;

@Component({
  selector: 'flow-hangfire-handoff',
  templateUrl: './hangfire-handoff.component.html',
  styleUrls: ['./hangfire-handoff.component.scss'],
})
export class HangfireHandoffComponent implements OnInit {
  state: HandoffState = 'opening';

  constructor(private authService: AuthService, private router: Router) {}

  ngOnInit(): void {
    const storedAuth = this.readStoredAuth();
    if (!storedAuth?.token || this.authService.tokenExpired(storedAuth)) {
      this.router.navigateByUrl('/login');
      return;
    }

    const user = this.authService.userData;
    if (user?.roleId !== UserRoleEnum.Admin || !!user?.currentCustomerId) {
      this.state = 'notAdmin';
      return;
    }

    if (this.attemptedRecently()) {
      this.clearAttempt();
      this.state = 'failed';
      return;
    }

    this.markAttempt();
    this.postTokenToApi(storedAuth.token);
  }

  private readStoredAuth(): UserLoginResponse | null {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY_AUTH));
    } catch {
      return null;
    }
  }

  private attemptedRecently(): boolean {
    try {
      const attemptedAt = Number(sessionStorage.getItem(ATTEMPT_KEY));
      return !!attemptedAt && Date.now() - attemptedAt < ATTEMPT_WINDOW_MS;
    } catch {
      return false;
    }
  }

  private markAttempt(): void {
    try {
      sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()));
    } catch {
      // Storage unavailable: the hand-off still works, it just loses its loop guard.
    }
  }

  private clearAttempt(): void {
    try {
      sessionStorage.removeItem(ATTEMPT_KEY);
    } catch {
      // As above.
    }
  }

  private postTokenToApi(accessToken: string): void {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = `${environment.apiBaseUrl}/hangfire-session`;

    const tokenField = document.createElement('input');
    tokenField.type = 'hidden';
    tokenField.name = 'accessToken';
    tokenField.value = accessToken;

    form.appendChild(tokenField);
    document.body.appendChild(form);
    form.submit();
  }
}
